import React, { useState } from "react";
import { motion } from "framer-motion";

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Using Formspree for reliable form submission
      const response = await fetch('https://formspree.io/f/xblkeeep', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          _subject: `Portfolio Contact from ${formData.name}`,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: "", email: "", message: "" });
      } else {
        throw new Error('Form submission failed');
      }
      
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Fallback: Open email client as backup
      const subject = `Portfolio Contact from ${formData.name}`;
      const body = `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`;
      const mailtoLink = `mailto:haris.butt0014@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(mailtoLink);
      
      setSubmitStatus('success'); // Show success since we opened email client
      setFormData({ name: "", email: "", message: "" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const socialLinks = [
    {
      name: "Email",
      icon: "📧",
      value: "haris.butt0014@gmail.com",
      href: "mailto:haris.butt0014@gmail.com",
      description: "Drop me a line"
    },
    {
      name: "LinkedIn",
      icon: "💼",
      value: "/in/harisbutt05",
      href: "https://linkedin.com/in/harisbutt05",
      description: "Let's connect professionally"
    },
    {
      name: "WhatsApp",
      icon: "📱",
      value: "+92-323-4134622",
      href: "https://wa.me/923234134622",
      description: "Let's chat"
    },
    {
      name: "GitHub",
      icon: "🐙",
      value: "@harisbutt",
      href: "https://github.com/Haris014",
      description: "Check out my code"
    },
    {
      name: "Twitter",
      icon: "🐦",
      value: "@harisbutt",
      href: "https://x.com/haris_butt94571",
      description: "Follow my thoughts"
    }
  ];

  const containerVariants = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    initial: { opacity: 0, y: 30 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const formVariants = {
    initial: { opacity: 0, x: -30 },
    animate: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const contactLinkVariants = {
    initial: { opacity: 0, x: 30 },
    animate: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div 
      className="max-w-4xl mx-auto"
      variants={containerVariants}
      initial="initial"
      animate="animate"
    >
      <motion.div 
        className="text-center mb-8 sm:mb-12"
        variants={itemVariants}
      >
        <motion.h2 
          className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4"
          whileHover={{ scale: 1.02 }}
        >
          Contact Us
        </motion.h2>
        <motion.p 
          className="text-sm sm:text-base md:text-lg text-gray-600"
          whileHover={{ color: "#374151" }}
          transition={{ duration: 0.3 }}
        >
          Ready to start a project or just want to chat? I'd love to hear from you.
        </motion.p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Contact Form */}
        <motion.div 
          className="bg-gray-50 rounded-2xl p-8"
          variants={formVariants}
          whileHover={{ 
            scale: 1.01,
            transition: { duration: 0.3 }
          }}
        >
          <motion.h3 
            className="text-xl font-semibold text-gray-900 mb-6"
            whileHover={{ x: 5 }}
            transition={{ duration: 0.2 }}
          >
            Send me a message
          </motion.h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Name
              </label>
              <motion.input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                placeholder="John Doe"
                whileFocus={{ scale: 1.01 }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <motion.input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                placeholder="john@example.com"
                whileFocus={{ scale: 1.01 }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <motion.textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none"
                placeholder="Tell me about your project..."
                whileFocus={{ scale: 1.01 }}
              />
            </div>
            
            <motion.button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 px-6 rounded-xl font-medium transition-colors ${
                isSubmitting 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-black text-white hover:bg-gray-800'
              }`}
              whileHover={!isSubmitting ? { 
                scale: 1.02,
                transition: { duration: 0.2 }
              } : {}}
              whileTap={!isSubmitting ? { scale: 0.98 } : {}}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </motion.button>
            
            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl"
              >
                <p className="text-green-800 text-sm">
                  ✅ Thank you! Your message has been sent. I'll get back to you soon.
                </p>
              </motion.div>
            )}
            
            {submitStatus === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl"
              >
                <p className="text-red-800 text-sm">
                  ❌ Something went wrong. Please try again or contact me directly via email.
                </p>
              </motion.div>
            )}
          </form>
        </motion.div>

        {/* Contact Links */}
        <motion.div variants={contactLinkVariants}>
          <motion.h3 
            className="text-xl font-semibold text-gray-900 mb-6"
            whileHover={{ x: 5 }}
            transition={{ duration: 0.2 }}
          >
            Other ways to reach me
          </motion.h3>
          
          <div className="space-y-4">
            {socialLinks.map((link, index) => (
              <motion.a
                key={index}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group"
                whileHover={{ 
                  x: 5,
                  scale: 1.02,
                  transition: { duration: 0.2 }
                }}
                whileTap={{ scale: 0.98 }}
              >
                <motion.div 
                  className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform"
                  whileHover={{ rotate: 5 }}
                >
                  <span className="text-xl">{link.icon}</span>
                </motion.div>
                <div className="flex-1">
                  <motion.h4 
                    className="font-semibold text-gray-900"
                    whileHover={{ color: "#3b82f6" }}
                  >
                    {link.name}
                  </motion.h4>
                  <motion.p 
                    className="text-sm text-gray-600"
                    initial={{ opacity: 0.8 }}
                    whileHover={{ opacity: 1 }}
                  >
                    {link.description}
                  </motion.p>
                  <motion.p 
                    className="text-sm font-mono text-gray-500"
                    whileHover={{ color: "#374151" }}
                  >
                    {link.value}
                  </motion.p>
                </div>
                <motion.svg 
                  className="w-5 h-5 text-gray-400 group-hover:text-gray-600" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  initial={{ x: 0 }}
                  whileHover={{ x: 3 }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </motion.svg>
              </motion.a>
            ))}
          </div>
        </motion.div>
      </div>

      {/* FAQ Section */}
      <motion.div 
        className="mt-16 p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl"
        variants={itemVariants}
        whileHover={{ 
          scale: 1.01,
          transition: { duration: 0.3 }
        }}
      >
        <motion.h3 
          className="text-xl font-semibold text-gray-900 mb-8 text-center"
          whileHover={{ scale: 1.02 }}
        >
          Frequently Asked Questions
        </motion.h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div 
            className="bg-white rounded-xl p-6"
            whileHover={{ 
              y: -3,
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              transition: { duration: 0.2 }
            }}
          >
            <motion.h4 
              className="font-semibold text-gray-900 mb-2"
              whileHover={{ color: "#3b82f6" }}
            >
              What's your typical response time?
            </motion.h4>
            <motion.p 
              className="text-gray-600 text-sm"
              initial={{ opacity: 0.8 }}
              whileHover={{ opacity: 1 }}
            >
              I usually respond to emails within 24 hours during weekdays.
            </motion.p>
          </motion.div>
          
          <motion.div 
            className="bg-white rounded-xl p-6"
            whileHover={{ 
              y: -3,
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              transition: { duration: 0.2 }
            }}
          >
            <motion.h4 
              className="font-semibold text-gray-900 mb-2"
              whileHover={{ color: "#3b82f6" }}
            >
              Do you work on weekends?
            </motion.h4>
            <motion.p 
              className="text-gray-600 text-sm"
              initial={{ opacity: 0.8 }}
              whileHover={{ opacity: 1 }}
            >
              I'm available for urgent matters, but prefer to discuss project timelines beforehand.
            </motion.p>
          </motion.div>
          
          <motion.div 
            className="bg-white rounded-xl p-6"
            whileHover={{ 
              y: -3,
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              transition: { duration: 0.2 }
            }}
          >
            <motion.h4 
              className="font-semibold text-gray-900 mb-2"
              whileHover={{ color: "#3b82f6" }}
            >
              What's your hourly rate?
            </motion.h4>
            <motion.p 
              className="text-gray-600 text-sm"
              initial={{ opacity: 0.8 }}
              whileHover={{ opacity: 1 }}
            >
              My rates vary by project complexity. Let's discuss your needs first.
            </motion.p>
          </motion.div>
          
          <motion.div 
            className="bg-white rounded-xl p-6"
            whileHover={{ 
              y: -3,
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              transition: { duration: 0.2 }
            }}
          >
            <motion.h4 
              className="font-semibold text-gray-900 mb-2"
              whileHover={{ color: "#3b82f6" }}
            >
              Do you offer ongoing support?
            </motion.h4>
            <motion.p 
              className="text-gray-600 text-sm"
              initial={{ opacity: 0.8 }}
              whileHover={{ opacity: 1 }}
            >
              Yes! I provide post-launch support and maintenance packages.
            </motion.p>
          </motion.div>
        </div>
      </motion.div>

      {/* Footer */}
      <motion.div 
        className="text-center mt-12 pt-8 border-t border-gray-200"
        variants={itemVariants}
      >
        <motion.p 
          className="text-sm text-gray-500 mt-2"
          initial={{ opacity: 0.6 }}
          whileHover={{ opacity: 1 }}
        >
          © 2025 Haris Butt. All rights reserved.
        </motion.p>
      </motion.div>
    </motion.div>
  );
} 